/**
 * src/components/Dashboard/People/PersonDetailModal.jsx
 * Read-only modal che mostra i dettagli di una persona
 */
import { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import '../../../css/personDetailModal.css'

const PersonDetailModal = ({ person, onClose }) => {
  const contentRef = useRef(null)
  const [isCompact, setIsCompact] = useState(false)

  // Blocca scroll del body quando il modal è aperto
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = original }
  }, [])

  // Chiudi il modal quando si clicca fuori dal contenuto (anche su nav, tab, etc.)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contentRef.current && !contentRef.current.contains(e.target)) {
        onClose()
      }
    }
    // Usa capture per intercettare il click prima che altri elementi lo gestiscano
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [onClose])

  // Rileva se il modal è stretto e applica layout compatto
  useEffect(() => {
    if (!contentRef.current) return
    const checkWidth = () => {
      const width = contentRef.current?.offsetWidth || 0
      setIsCompact(width < 400)
    }
    checkWidth()
    const observer = new ResizeObserver(checkWidth)
    observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [])

  if (!person) return null

  // Handle both camelCase and snake_case
  const dataNascita = person.dataNascita || person.data_nascita
  const description = person.description || person.description
  const qualifications = person.qualifications || person.qualifications
  const imageUrl = person.imageUrl || person.image_url
  const phone = person.phone || person.phone
  const email = person.email || person.email
  const sesso = person.sesso || person.sesso

  const birthDate = dataNascita
    ? new Date(dataNascita).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : '-'

  return ReactDOM.createPortal(
    <div className="person-detail-modal-backdrop" onClick={onClose}>
      <div
        ref={contentRef}
        className={`person-detail-modal-content${isCompact ? ' compact-layout' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="person-detail-header">
          <img
            src={imageUrl || 'https://via.placeholder.com/120'}
            alt={`${person.nome} ${person.cognome}`}
            className="person-detail-image"
          />
          <div className="person-detail-header-info">
            <h2>{person.nome} {person.cognome}</h2>
            {phone && <p className="phone-info">📱 {phone}</p>}
            {email && <p className="email-info">✉️ {email}</p>}
          </div>
        </div>

        <div className="person-detail-body">
          <div className="detail-section">
            <h4>Informazioni Personali</h4>
            <div className="detail-row">
              <span className="label">Data di nascita:</span>
              <span className="value">{birthDate === '-' ? 'mancante' : birthDate}</span>
            </div>
            <div className="detail-row">
              <span className="label">Sesso:</span>
              <span className="value">{sesso ? sesso : 'mancante'}</span>
            </div>
          </div>

          {description && (
            <div className="detail-section">
              <h4>Descrizione</h4>
              <p className="description-text">{description}</p>
            </div>
          )}

          {qualifications && qualifications.length > 0 && (
            <div className="detail-section">
              <h4>Qualifiche</h4>
              <div className="qualifications-list">
                {qualifications.map(q => (
                  <span key={q.id} className="qualification-tag">
                    {q.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PersonDetailModal
